import { collection, getDocs, query, where, type QueryDocumentSnapshot } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';

import { questionnaireVersion, politicalMatchQuestions as questionCatalog, type PoliticalMatchQuestion as PoliticalMatchQuestionSeed } from '@/data/matchPoliticoMethodology';
import { getFirebaseEnvConfig } from '@/lib/firebase/config';
import { getFirebaseFirestore } from '@/lib/firebase/firestore';
import { getFirebaseFunctions } from '@/lib/firebase/functions';
import { buildPoliticalMatchMatchesQueryKey, buildPoliticalMatchQuestionsQueryKey } from '@/lib/query-cache/query-keys';
import { resolveBrowserQueryCache, type BrowserQueryCache } from '@/lib/query-cache/query-cache';
import type { PoliticalMatchQuestion } from '@/models/political-match';
import type {
  GetPoliticalMatchesResponse,
  PoliticalMatchQuestionDoc,
  SubscribePoliticalMatchNewsletterRequest,
  SubscribePoliticalMatchNewsletterResponse,
} from '@/types/backend-contracts';

function hasFirebaseWebConfig(): boolean {
  const config = getFirebaseEnvConfig();

  return Boolean(
    config.apiKey &&
      config.authDomain &&
      config.projectId &&
      config.storageBucket &&
      config.messagingSenderId &&
      config.appId,
  );
}

function toAxis(axis: PoliticalMatchQuestionSeed['axis']): PoliticalMatchQuestion['axis'] {
  return axis === 'liberdade-economica' ? 'economic' : 'personal';
}

function toPoliticalMatchQuestionModel(
  question: PoliticalMatchQuestionSeed,
  order: number,
  version = questionnaireVersion,
): PoliticalMatchQuestion {
  return {
    id: question.id,
    version,
    order,
    statement: question.question,
    axis: toAxis(question.axis),
    dimension: question.theme,
    direction: question.direction,
    weight: question.weight,
    isQuick: Boolean(question.isQuickQuestion),
    isActive: true,
    methodologySourceIds: [...question.methodologySourceIds],
  };
}

function toPoliticalMatchQuestionFromDoc(question: PoliticalMatchQuestionDoc & { id: string }): PoliticalMatchQuestion {
  return {
    id: question.id,
    version: question.version,
    order: question.order,
    statement: question.statement,
    axis: question.axis,
    dimension: question.dimension,
    direction: question.direction,
    weight: question.weight,
    isQuick: question.isQuick,
    isActive: question.isActive,
    methodologySourceIds: [...question.methodologySourceIds],
  };
}

export const politicalMatchQuestionFallbacks: PoliticalMatchQuestion[] = questionCatalog.map((question, index) =>
  toPoliticalMatchQuestionModel(question, index + 1),
);

async function loadPoliticalMatchQuestionsFromFirestore(): Promise<PoliticalMatchQuestion[]> {
  if (!hasFirebaseWebConfig()) {
    return politicalMatchQuestionFallbacks;
  }

  try {
    const snapshot = await getDocs(query(collection(getFirebaseFirestore(), 'politicalMatchQuestions'), where('isActive', '==', true)));
    const questions = snapshot.docs
      .map((document: QueryDocumentSnapshot) => ({
        id: document.id,
        ...(document.data() as PoliticalMatchQuestionDoc),
      }))
      .sort((left, right) => {
        const orderComparison = left.order - right.order;

        if (orderComparison !== 0) {
          return orderComparison;
        }

        return left.id.localeCompare(right.id, 'pt-BR');
      })
      .map(toPoliticalMatchQuestionFromDoc);

    return questions.length > 0 ? questions : politicalMatchQuestionFallbacks;
  } catch {
    return politicalMatchQuestionFallbacks;
  }
}

export async function getPoliticalMatchQuestions(options?: { cache?: BrowserQueryCache }): Promise<PoliticalMatchQuestion[]> {
  const cache = resolveBrowserQueryCache(options?.cache);

  const result = await cache.read(
    buildPoliticalMatchQuestionsQueryKey(),
    async () => loadPoliticalMatchQuestionsFromFirestore(),
    {
      staleTimeMs: 5 * 60 * 1000,
      cacheTimeMs: 30 * 60 * 1000,
      tags: ['political-match:questions'],
    },
  );

  return result.value;
}

export async function subscribePoliticalMatchNewsletter(
  request: SubscribePoliticalMatchNewsletterRequest,
): Promise<SubscribePoliticalMatchNewsletterResponse> {
  const callable = httpsCallable<SubscribePoliticalMatchNewsletterRequest, SubscribePoliticalMatchNewsletterResponse>(
    getFirebaseFunctions(),
    'subscribePoliticalMatchNewsletter',
  );

  const response = await callable({
    email: request.email.trim(),
    consent: request.consent,
    consentVersion: request.consentVersion.trim(),
  });

  return response.data;
}

export async function getPoliticalMatches(options?: { cache?: BrowserQueryCache }): Promise<GetPoliticalMatchesResponse> {
  const cache = resolveBrowserQueryCache(options?.cache);

  const result = await cache.read(
    buildPoliticalMatchMatchesQueryKey(questionnaireVersion),
    async () => {
      const callable = httpsCallable<void, GetPoliticalMatchesResponse>(getFirebaseFunctions(), 'getPoliticalMatches');

      const response = await callable();
      return response.data;
    },
    {
      staleTimeMs: 5 * 60 * 1000,
      cacheTimeMs: 30 * 60 * 1000,
      tags: ['political-match:matches'],
    },
  );

  return result.value;
}
